import SVGIcon from 'components/SVGIcon/SVGIcon';
import VisuallyHidden from 'components/VisuallyHidden';
import { getImageByCategory } from 'containers/MarketPage/ImageByCategory';
import useProductUomInfo from 'hooks/useProductUomInfo';
import React, { FC, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Mousewheel } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Language, PriceV2, Product } from 'types/schema';
import calculateVAT from 'utils/calculateVAT';
import Money from 'components/Money/Money';
import utilMessages from 'utils/messages';
import generateProductSlug from '../../../utils/generateProductSlug';
import { EventName, sendGA4Event } from '../../../utils/triggerGA4Event';
import { Image } from '../../MightFavoriteProductsWidget/Image';
import CheckIcon from '../_icons/CheckIcon.svg';
import chevronLeftIcon from '../_icons/chevronLeft.svg';
import chevronRightIcon from '../_icons/chevronRight.svg';
import DiscountBackground from '../_icons/DiscountBackground.png';
import KamereoProductBackground from '../_icons/KamereoPromotion.svg';
import { FrameImage } from '../ProductImagesModal';
import TagImage from '../_icons/TagImage.png';

const PromotionWrapper = styled.div`
  background: linear-gradient(295deg, #e3ffe4 8.83%, #f0fff0 92.39%);
  border-radius: 8px;
  max-height: 477px;
  position: relative;
`;

const KamereoBackgroundImage = styled.div`
  position: absolute;
  top: 37px;
  right: 0px;
  z-index: 0;
  @media (max-width: 576px) {
    top: 40px;
    right: -25px;
    opacity: 0.5;
    & img {
      width: 100px;
    }
  }
`;

const PromotionCornerTitle = styled.div`
  background: #f38a3e;
  padding: 6px 12px;
  display: flex;
  gap: 4px;
  width: fit-content;
  border-radius: 8px 0 12px 0;
  @media (max-width: 576px) {
    border-radius: 0px 0 12px 0;
  }
`;

const PromotionCornerTitleText = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
`;

const PromotionTitleWrapper = styled.div`
  margin: 8px 16px 16px;
  position: relative;
`;

const PromotionTitleBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PromotionTitleImage = styled.img`
  width: 20px;
  object-fit: cover;
`;

const PromotionTitleText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #2f382c;
`;

const PromotionTitleSubText = styled.div`
  font-size: 14px;
  color: #596056;
`;

const ImageContainer = styled.div`
  position: relative;
`;

const PromotionKamereoProductList = styled.div`
  position: relative;
  margin-left: 16px;
  max-height: 378px;
  padding-bottom: 16px;
`;

const PromotionKamereoProductItem = styled(Link)`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
  width: 156px;
  height: 100%;
`;

const ProductInfo = styled.div`
  padding: 11px 12px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  @media (max-width: 576px) {
    padding: 8px;
  }
`;

const ProductPriceWrapper = styled.div`
  background: #ffecea;
  border-radius: 4px;
  height: 24px;
  display: flex;
  align-items: center;
  ${({ fitContent }) =>
    fitContent &&
    `
    width: fit-content;
  `}
`;

const ProductPriceValue = styled.div`
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 18px;
  color: #f25b50;
  margin: 0 8px;
  @media (max-width: 576px) {
    font-size: 12px;
  }
`;

const ProductPriceDiscountPercentage = styled.div`
  background-image: url(${DiscountBackground});
  width: 100%;
  height: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: right;
`;

const ProductPriceDiscountPercentageText = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: end;
  margin-right: 8px;
  height: 100%;
  @media (max-width: 576px) {
    font-size: 10px;
  }
`;

const ProductName = styled.span`
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: #2f382c;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  margin-top: 4px;
`;

const ProductUom = styled.div`
  color: #808289;
  font-size: 12px;
  font-weight: 500;
  margin-top: auto;
`;

const SwiperElement = styled(Swiper)`
  padding-right: 16px !important;
  .swiper-button-next,
  .swiper-button-prev {
    display: none !important;
  }
`;

const SwiperSlideElement = styled(SwiperSlide)`
  width: fit-content !important;
  height: auto !important;
`;

const LeftIconContainer = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: #fffff;
  left: -31px;
  width: 30px;
  height: 30px;
  background: #ffffff;
  padding: 8px 12px;
  cursor: pointer;
  border: 1px solid #d5d7d5;
  border-radius: 50%;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f0f9f1;
    transition: background 0.2s;
  }
`;

const RightIconContainer = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: #ffffff;
  right: -15px;
  width: 30px;
  height: 30px;
  background: #fffff;
  padding: 8px 12px;
  cursor: pointer;
  border: 1px solid #d5d7d5;
  border-radius: 50%;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f0f9f1;
    transition: background 0.2s;
  }
`;

const KamereoProductItem: FC<{ kamereoProduct: Product; productId?: string }> = ({ kamereoProduct, productId }) => {
  const { uomInfo } = useProductUomInfo(kamereoProduct);
  const lang = localStorage.getItem('lang') || Language.Vi;
  const frameTagImage = lang === 'vi' ? kamereoProduct.tagImageVn : kamereoProduct.tagImageEn || '';
  const generateImage = (product) => {
    return product?.category && !product?.imageUrl ? getImageByCategory(product?.category) : product?.imageUrl;
  };
  const calculateDiscountPercentageInKamereoProduct = (priceParams: PriceV2) => {
    if (!priceParams) {
      return 0;
    }
    const { price, originalPrice, vat, vatIncluded } = priceParams;
    if (price === originalPrice) return 0;
    const priceBeforeVat = calculateVAT(price, vat, vatIncluded).beforeVat;
    const originalPriceBeforeVat = calculateVAT(originalPrice || 0, vat, vatIncluded).beforeVat;

    return 100 - Math.round((priceBeforeVat * 100) / originalPriceBeforeVat);
  };
  return (
    <PromotionKamereoProductItem
      to={`/products/${generateProductSlug(kamereoProduct?.name, kamereoProduct?.supplierInternalCode, lang)}`}
      onClick={() => {
        sendGA4Event(EventName.KMR_PB_CLICK);
        sendGA4Event(EventName.PDP_CLICK_PRIVATE_LABEL, {
          from_product_id: productId,
          to_product_id: kamereoProduct.id,
          product_group: kamereoProduct.productGroups || [],
        });
      }}
    >
      <ImageContainer>
        <Image src={generateImage(kamereoProduct)} alt={String(name)} size={156} noBorder />
        {frameTagImage && <FrameImage src={frameTagImage} />}
        <VisuallyHidden asComponent="span">{kamereoProduct?.name}</VisuallyHidden>
      </ImageContainer>
      <ProductInfo>
        <ProductPriceWrapper
          fitContent={!calculateDiscountPercentageInKamereoProduct(kamereoProduct?.priceV2 as PriceV2)}
        >
          <ProductPriceValue>
            <Money>
              {calculateVAT(
                kamereoProduct?.priceV2?.price || 0,
                kamereoProduct?.priceV2?.vat || 0,
                !!kamereoProduct?.priceV2?.vatIncluded,
              ).beforeVat}
            </Money>
          </ProductPriceValue>
          {!!calculateDiscountPercentageInKamereoProduct(kamereoProduct?.priceV2 as PriceV2) && (
            <ProductPriceDiscountPercentage>
              <ProductPriceDiscountPercentageText>{`-${calculateDiscountPercentageInKamereoProduct(kamereoProduct?.priceV2 as PriceV2)}%`}</ProductPriceDiscountPercentageText>
            </ProductPriceDiscountPercentage>
          )}
        </ProductPriceWrapper>
        <ProductName>{kamereoProduct.name}</ProductName>
        <ProductUom>{uomInfo}</ProductUom>
      </ProductInfo>
    </PromotionKamereoProductItem>
  );
};

const KamereoProductList: FC<{ kamereoProducts: Product[]; productId?: string }> = ({ kamereoProducts, productId }) => {
  const swiperRef = useRef<any>(null);
  const [isShowPrev, setIsShowPrev] = useState(false);
  const [isShowNext, setIsShowNext] = useState(false);

  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef?.current?.slidePrev();
    }
  };

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current?.slideNext();
    }
  };

  return (
    <PromotionWrapper>
      <KamereoBackgroundImage>
        <SVGIcon src={KamereoProductBackground} />
      </KamereoBackgroundImage>
      <PromotionCornerTitle>
        <SVGIcon src={CheckIcon} />
        <PromotionCornerTitleText>
          <FormattedMessage {...utilMessages.promotionKamereo} />
        </PromotionCornerTitleText>
      </PromotionCornerTitle>
      <PromotionTitleWrapper>
        <PromotionTitleBox>
          <PromotionTitleImage src={TagImage} />
          <PromotionTitleText>
            <FormattedMessage {...utilMessages.promotionKamereoTitle} />
          </PromotionTitleText>
        </PromotionTitleBox>
        <PromotionTitleSubText>
          <FormattedMessage {...utilMessages.promotionKamereoSubTitle} />
        </PromotionTitleSubText>
      </PromotionTitleWrapper>
      <PromotionKamereoProductList>
        <SwiperElement
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          spaceBetween={12}
          navigation={false}
          mousewheel={{ forceToAxis: true }}
          simulateTouch={true}
          modules={[Mousewheel]}
          pagination={false}
          slidesPerView={'auto'}
          className="mySwiper"
          slidesPerGroup={2}
          grabCursor={true}
          centeredSlides={false}
          onReachEnd={() => {
            if (!isShowNext) return;
            setIsShowNext(false);
            setIsShowPrev(true);
          }}
          onReachBeginning={() => {
            if (!isShowPrev) return;
            setIsShowPrev(false);
            setIsShowNext(true);
          }}
          onSlideChange={(swiper) => {
            if (swiper?.isBeginning || swiper?.isEnd) {
              return;
            }
            if (!swiper?.isBeginning !== isShowPrev) {
              setIsShowPrev((prev) => !prev);
            }
            if (!swiper?.isEnd !== isShowNext) {
              setIsShowNext((prev) => !prev);
            }
          }}
          onInit={(swiper) => {
            if (!swiper?.isBeginning !== isShowPrev) {
              setIsShowPrev((prev) => !prev);
            }
            if (!swiper?.isEnd !== isShowNext) {
              setIsShowNext((prev) => !prev);
            }
          }}
        >
          {kamereoProducts?.map((product: Product, index: number) => {
            return (
              <SwiperSlideElement key={product?.id || index}>
                <KamereoProductItem kamereoProduct={product} productId={productId} />
              </SwiperSlideElement>
            );
          })}
        </SwiperElement>
        {isShowPrev && (
          <LeftIconContainer onClick={handlePrev}>
            <SVGIcon src={chevronLeftIcon} renderAsImg={false} />
          </LeftIconContainer>
        )}

        {isShowNext && (
          <RightIconContainer onClick={handleNext}>
            <SVGIcon src={chevronRightIcon} renderAsImg={false} />
          </RightIconContainer>
        )}
      </PromotionKamereoProductList>
    </PromotionWrapper>
  );
};

export default KamereoProductList;
